import {otsBaseApi} from "../../ots-api";
import {
  AttachmentBundle,
  AttachmentFile,
  AttachmentMetadataPatch,
  AttachmentTarget,
  AttachmentUpdateRequest,
  AttachmentUploadItem,
} from "../../../model/attachment-file";
import {buildPatchDto} from "../../../model/patch-value";
import {removeUndefined} from "../../../util/util";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ["AttachmentFiles", "ObjectAttachmentFiles"] as const,
});

const targetKey = ({objectType, objectId}: AttachmentTarget) => `${objectType}:${objectId}`;

const attachmentListRequest = ({objectType, objectId, tenantCode}: AttachmentTarget) => ({
  url: `/attachment-files/${encodeURIComponent(objectType)}/${encodeURIComponent(objectId)}`,
  method: "GET",
  params: removeUndefined({tenantCode}),
});

export type TargetAttachmentBundle = {
  target: AttachmentTarget;
  bundle: AttachmentBundle;
};

export const buildAttachmentUploadFormData = ({target, file, metadata}: AttachmentUploadItem): FormData => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("meta", new Blob([JSON.stringify({
    objectId: target.objectId,
    objectType: target.objectType,
    ...metadata,
  })], {type: "application/json"}));
  return formData;
};

export const buildAttachmentBulkUploadFormData = (items: AttachmentUploadItem[]): FormData => {
  const formData = new FormData();
  const manifest = {
    items: items.map((item, index) => {
      const id = item.clientId ?? `attachment-${index + 1}`;
      formData.append(id, item.file);
      return {
        id,
        objectId: item.target.objectId,
        objectType: item.target.objectType,
        ...item.metadata,
      };
    }),
  };
  formData.append("manifest", new Blob([JSON.stringify(manifest)], {type: "application/json"}));
  return formData;
};

export const buildAttachmentMetadataPatch = (
  changes: AttachmentUpdateRequest["changes"],
): AttachmentMetadataPatch => buildPatchDto<AttachmentFile>(changes) as AttachmentMetadataPatch;

export const buildAttachmentMetadataPatchRequest = ({attachment, changes}: AttachmentUpdateRequest) => ({
  url: `/attachment-files/${encodeURIComponent(attachment.id)}`,
  method: "PATCH",
  body: buildAttachmentMetadataPatch(changes),
});

export const buildAttachmentUpdateInvalidationTags = (
  result: AttachmentFile | undefined,
  error: unknown,
  {attachment}: AttachmentUpdateRequest,
) => error ? [] : [
  {type: "AttachmentFiles" as const, id: result?.id ?? attachment.id},
  {type: "ObjectAttachmentFiles" as const, id: targetKey(result ?? attachment)},
];

export const attachmentFilesApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAttachmentBundle: builder.query<AttachmentBundle, AttachmentTarget>({
      query: attachmentListRequest,
      providesTags: (result, _error, target) => [
        ...(result?.attachments.map(({id}) => ({type: "AttachmentFiles" as const, id})) ?? []),
        {type: "ObjectAttachmentFiles" as const, id: targetKey(target)},
      ],
    }),
    getAttachmentBundles: builder.query<TargetAttachmentBundle[], AttachmentTarget[]>({
      queryFn: async (targets, _api, _options, baseQuery) => {
        const responses = await Promise.all(targets.map((target) => baseQuery(attachmentListRequest(target))));
        const failed = responses.find((response) => response.error);
        if (failed?.error) return {error: failed.error};

        return {
          data: responses.map((response, index) => ({
            target: targets[index],
            bundle: response.data as AttachmentBundle,
          })),
        };
      },
      providesTags: (result, _error, targets) => [
        ...(result?.flatMap(({bundle}) => bundle.attachments.map(({id}) => ({type: "AttachmentFiles" as const, id}))) ?? []),
        ...targets.map((target) => ({type: "ObjectAttachmentFiles" as const, id: targetKey(target)})),
      ],
    }),
    uploadAttachment: builder.mutation<AttachmentFile, AttachmentUploadItem>({
      query: (item) => ({
        url: "/attachment-files/upload",
        method: "POST",
        body: buildAttachmentUploadFormData(item),
      }),
      invalidatesTags: (_result, _error, {target}) => [
        {type: "ObjectAttachmentFiles", id: targetKey(target)},
      ],
    }),
    uploadAttachments: builder.mutation<AttachmentFile[], AttachmentUploadItem[]>({
      query: (items) => ({
        url: "/attachment-files/bulk-upload",
        method: "POST",
        body: buildAttachmentBulkUploadFormData(items),
      }),
      invalidatesTags: (_result, _error, items) => [
        ...new Set(items.map(({target}) => targetKey(target))),
      ].map((id) => ({type: "ObjectAttachmentFiles" as const, id})),
    }),
    updateAttachment: builder.mutation<AttachmentFile, AttachmentUpdateRequest>({
      query: buildAttachmentMetadataPatchRequest,
      // Preserve the editor and its inline error when PATCH fails. Invalidating on an error starts a
      // list refetch, which temporarily unmounts AttachmentList and discards that feedback.
      invalidatesTags: buildAttachmentUpdateInvalidationTags,
    }),
    deleteAttachment: builder.mutation<void, AttachmentFile>({
      query: (attachment) => ({
        url: "/attachment-files",
        method: "DELETE",
        body: attachment,
      }),
      invalidatesTags: (_result, _error, attachment) => [
        {type: "AttachmentFiles", id: attachment.id},
        {type: "ObjectAttachmentFiles", id: targetKey(attachment)},
      ],
    }),
  }),
});

export const {
  useGetAttachmentBundleQuery,
  useGetAttachmentBundlesQuery,
  useLazyGetAttachmentBundleQuery,
  useLazyGetAttachmentBundlesQuery,
  useUploadAttachmentMutation,
  useUploadAttachmentsMutation,
  useUpdateAttachmentMutation,
  useDeleteAttachmentMutation,
} = attachmentFilesApi;
